import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* ── API ── */
const API_BASE = import.meta.env.VITE_API_URL || "https://daroulmesbackend.onrender.com";
const WA_PHONE  = import.meta.env.VITE_WA_PHONE  || "212642186606"; // set in .env

function getAuthUser() {
  try {
    // !JUST FIXED — Register.jsx / Login.jsx store the user under "user"
    // (with the token under "access_token"). The old "dar_oulmes_user" key
    // was never written, so user?.id was always undefined and userId ended
    // up null in the Order row. Read the real keys, with the legacy key as
    // a fallback in case some older builds still wrote it.
    const raw =
      localStorage.getItem("user") ||
      sessionStorage.getItem("user") ||
      localStorage.getItem("dar_oulmes_user") ||
      sessionStorage.getItem("dar_oulmes_user");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function getSessionId() {
  let sid = localStorage.getItem("dar_oulmes_session");
  if (!sid) {
    sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem("dar_oulmes_session", sid);
  }
  return sid;
}

/* ── SVG icons ── */
const LockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
  </svg>
);
const ShieldIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#25D366">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2d6a4f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const SpinnerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: "co-spin 1s linear infinite" }}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
  </svg>
);

/* ── Map Styles ── */
const MAP_STYLES = [
  { id: "streets",   labelKey: "checkout.map_style_streets",   url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",                                                         maxZoom: 19 },
  { id: "satellite", labelKey: "checkout.map_style_satellite", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",                 maxZoom: 19 },
  { id: "dark",      labelKey: "checkout.map_style_dark",      url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",                                                maxZoom: 19 },
  { id: "terrain",   labelKey: "checkout.map_style_terrain",   url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",                                                             maxZoom: 17 },
];

/* ── MapPicker ── */
function MapPicker({ onLocationSelect }) {
  const { t } = useTranslation();
  const mapRef         = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef      = useRef(null);
  const tileLayerRef   = useRef(null);

  const [locating,    setLocating]   = useState(false);
  const [locError,    setLocError]   = useState("");
  const [locLabel,    setLocLabel]   = useState("");
  const [activeStyle, setActiveStyle]= useState("streets");

  useEffect(() => {
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css"; link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    const init = () => {
      if (mapInstanceRef.current || !mapRef.current) return;
      const L   = window.L;
      const map = L.map(mapRef.current, { center: [34.0209, -6.8416], zoom: 14, zoomControl: false });
      L.control.zoom({ position: "bottomright" }).addTo(map);
      tileLayerRef.current = L.tileLayer(MAP_STYLES[0].url, { maxZoom: 19 }).addTo(map);
      mapInstanceRef.current = map;
      map.on("click", e => { placeMarker(e.latlng.lat, e.latlng.lng); reverseGeocode(e.latlng.lat, e.latlng.lng); });
    };
    if (window.L) { init(); }
    else {
      const s = document.createElement("script");
      s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      s.onload = init; document.head.appendChild(s);
    }
    return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
  }, []);

  const switchStyle = (id) => {
    const L = window.L; const map = mapInstanceRef.current;
    if (!map || !L) return;
    const st = MAP_STYLES.find(s => s.id === id);
    if (!st) return;
    if (tileLayerRef.current) map.removeLayer(tileLayerRef.current);
    tileLayerRef.current = L.tileLayer(st.url, { maxZoom: st.maxZoom }).addTo(map);
    setActiveStyle(id);
  };

  const placeMarker = (lat, lng) => {
    const L = window.L; const map = mapInstanceRef.current;
    if (!map) return;
    const goldIcon = L.divIcon({
      className: "",
      html: `<div style="width:36px;height:46px;filter:drop-shadow(0 4px 8px rgba(0,0,0,.35))">
        <svg viewBox="0 0 36 46" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 0C8.059 0 0 8.059 0 18c0 13 18 28 18 28s18-15 18-28C36 8.059 27.941 0 18 0z" fill="#c9a84c"/>
          <path d="M18 1C8.611 1 1 8.611 1 18c0 12.5 17 27 17 27s17-14.5 17-27C35 8.611 27.389 1 18 1z" fill="#d4b460"/>
          <circle cx="18" cy="18" r="9" fill="white" opacity=".95"/>
          <circle cx="18" cy="18" r="5.5" fill="#c9a84c"/>
          <circle cx="18" cy="18" r="2.5" fill="white"/>
        </svg></div>`,
      iconSize: [36, 46], iconAnchor: [18, 46], popupAnchor: [0, -46],
    });
    if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
    else markerRef.current = L.marker([lat, lng], { icon: goldIcon }).addTo(map);
    map.setView([lat, lng], 17);
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const res  = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
      const data = await res.json();
      const label = data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      setLocLabel(label);
      if (onLocationSelect) onLocationSelect({ lat, lng, label });
    } catch {
      const label = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      setLocLabel(label);
      if (onLocationSelect) onLocationSelect({ lat, lng, label });
    }
  };

  const detectLocation = () => {
    setLocError(""); setLocating(true);
    if (!navigator.geolocation) { setLocError(t("checkout.geoloc_not_supported")); setLocating(false); return; }
    navigator.geolocation.getCurrentPosition(
      pos => { placeMarker(pos.coords.latitude, pos.coords.longitude); reverseGeocode(pos.coords.latitude, pos.coords.longitude); setLocating(false); },
      ()  => { setLocError(t("checkout.geoloc_error")); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="co-map-wrap">
      <style>{`
        @keyframes co-spin { to { transform: rotate(360deg); } }
        .leaflet-control-zoom { border: none !important; border-radius: 10px !important; overflow: hidden; box-shadow: 0 2px 14px rgba(0,0,0,.18) !important; }
        .leaflet-control-zoom-in, .leaflet-control-zoom-out { background: rgba(255,252,244,.96) !important; color: #3a3220 !important; border: none !important; font-size: 18px !important; width: 34px !important; height: 34px !important; line-height: 34px !important; }
        .leaflet-control-zoom-in:hover, .leaflet-control-zoom-out:hover { background: #f5f0e8 !important; color: #c9a84c !important; }
      `}</style>

      {/* Header row */}
      <div className="co-map-header">
        <label className="co-field-label" style={{ marginBottom: 0 }}>{t("checkout.map_pin_title")}</label>
        <button className="co-locate-btn" onClick={detectLocation} disabled={locating}>
          {locating ? <><SpinnerIcon /> {t("checkout.locating")}</> : <>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
            </svg>
            {t("checkout.detect_location")}
          </>}
        </button>
      </div>

      {/* Map */}
      <div className="co-map-container">
        <div ref={mapRef} style={{ width: "100%", height: 280 }} />

        {/* Style switcher */}
        <div className="co-map-styles">
          {MAP_STYLES.map(st => (
            <button
              key={st.id}
              className={`co-map-style-btn${activeStyle === st.id ? " active" : ""}`}
              onClick={() => switchStyle(st.id)}
            >{t(st.labelKey)}</button>
          ))}
        </div>

        {/* Click hint */}
        <div className="co-map-hint">{t("checkout.click_map_hint")}</div>
      </div>

      {locError && <p className="co-loc-error">{locError}</p>}

      {locLabel && !locError && (
        <div className="co-loc-label">
          <span>📍</span>
          <span>{locLabel}</span>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN CHECKOUT COMPONENT
══════════════════════════════════════ */
export default function CheckoutPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate  = useNavigate();
  const user      = getAuthUser();

  /* ── Cart from navigation state or localStorage fallback ── */
  const stateData  = location.state || {};
  const [cart] = useState(() => {
    if (stateData.cart?.length) return stateData.cart;
    try { return JSON.parse(localStorage.getItem("dar_oulmes_cart") || "[]"); }
    catch { return []; }
  });

  const subtotal = stateData.subtotal ?? cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const tax      = stateData.tax      ?? Math.round(subtotal * 0.06);
  const discAmt  = stateData.discAmt  ?? 0;
  const total    = stateData.total    ?? subtotal + tax - discAmt;

  /* ── Form state ── */
  const [form, setForm] = useState({
    name:    user?.name    || "",
    email:   user?.email   || "",
    phone:   user?.phone   || "",
    address: "",
    city:    "",
    zip:     "",
    coupon:  "",
    save:    true,
    notes:   "",
  });
  const [location_pin, setLocationPin] = useState(null);
  const [couponMsg, setCouponMsg] = useState(null);
  const [extraDisc, setExtraDisc] = useState(0);

  const [step, setStep]         = useState(1); // 1=form, 2=confirm, 3=success
  const [placing, setPlacing]   = useState(false);
  const [orderRef, setOrderRef] = useState(null);
  const [errors, setErrors]     = useState({});

  const finalTotal = total - Math.round(subtotal * extraDisc / 100);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const applyCoupon = () => {
    if (form.coupon.toUpperCase() === "DAROULMES10") {
      setExtraDisc(10); setCouponMsg({ ok: true, text: t("checkout.coupon_applied") });
    } else { setExtraDisc(0); setCouponMsg({ ok: false, text: t("checkout.coupon_invalid") }); }
  };

  /* ── Validate form ── */
  const validate = () => {
    const errs = {};
    if (!form.name.trim())    errs.name    = t("checkout.err_name_required");
    if (!form.phone.trim())   errs.phone   = t("checkout.err_phone_required");
    if (!form.address.trim() && !location_pin) errs.address = t("checkout.err_address_required");
    return errs;
  };

  /* ── Step 1 → 2 (review) ── */
  const goReview = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ── Place order → API → WhatsApp ── */
  const placeOrder = async () => {
    setPlacing(true);
    try {
      const sessionId = getSessionId();

      /* 1. Upsert session */
      const expiry = new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString();
      await fetch(`${API_BASE}/api/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: sessionId,
          cartData: { items: cart },
          expiresAt: expiry,
          ...(user?.id ? { userId: user.id } : {}),
        }),
      });

      /* 2. Create order */
      // !JUST FIXED — compute the delivery address label and send lat/lng/address
      // at the top level so the server can persist them to the dedicated columns.
      // Falls back to the form address if no map pin was dropped.
      const addressLabel = location_pin
        ? location_pin.label
        : [form.address, form.city, form.zip].filter(Boolean).join(", ");

      const orderPayload = {
        sessionId,
        totalAmount: finalTotal,
        orderData: {
          items: cart,
          customer: { ...form, location: location_pin || null },
          subtotal, tax, discAmt: discAmt + Math.round(subtotal * extraDisc / 100),
          total: finalTotal,
        },
        ...(user?.id ? { userId: user.id } : {}),
        // !JUST FIXED — top-level delivery fields (deliveryLat / deliveryLng / deliveryAddress)
        deliveryLat:     location_pin ? location_pin.lat : null,
        deliveryLng:     location_pin ? location_pin.lng : null,
        deliveryAddress: addressLabel || null,
      };

      const res   = await fetch(`${API_BASE}/api/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderPayload),
      });
      const order = await res.json();

      if (!res.ok) throw new Error(order.error || "Order creation failed");

      setOrderRef(order.id || "REF-" + Date.now());

      /* 3. Clear cart */
      localStorage.setItem("dar_oulmes_cart", "[]");
      window.dispatchEvent(new Event("cart-updated"));

      /* 4. Build WhatsApp message */
      const addr = location_pin ? location_pin.label : [form.address, form.city, form.zip].filter(Boolean).join(", ");
      const totalDisc = discAmt + Math.round(subtotal * extraDisc / 100);
      const divider = "─────────────────────";

      const itemLines = cart.map((i, n) =>
        [
          `  ${n + 1}. ${i.name}`,
          `      • الكمية: ${i.quantity} قطعة`,
          `      • السعر: ${parseFloat(i.price).toFixed(2)} MAD / قطعة`,
          `      • الإجمالي: ${(i.price * i.quantity).toFixed(2)} MAD`,
        ].join("\n")
      ).join("\n\n");

      const lines = [
        `╔══════════════════════╗`,
        `   🛍️ طلب جديد — دار أولماس`,
        `╚══════════════════════╝`,
        ``,
        `رقم الطلب: #${order.id || "—"}`,
        ``,
        divider,
        `👤 معلومات العميل`,
        divider,
        `الاسم:       ${form.name}`,
        `الهاتف:      ${form.phone}`,
        `الإيميل:     ${form.email || "—"}`,
        ``,
        divider,
        `📍 عنوان التوصيل`,
        divider,
        addr,
        form.notes ? `\n📝 ملاحظات: ${form.notes}` : "",
        ``,
        divider,
        `🧾 المنتجات المطلوبة (${cart.length} صنف)`,
        divider,
        ``,
        itemLines,
        ``,
        divider,
        `💵 ملخص الدفع`,
        divider,
        `المجموع الفرعي:   ${subtotal.toFixed(2)} MAD`,
        `الشحن:            مجاني 🎁`,
        `الضريبة:          ${tax.toFixed(2)} MAD`,
        totalDisc > 0 ? `الخصم:            -${totalDisc.toFixed(2)} MAD ✂️` : "",
        ``,
        `💰 المجموع الكلي:  ${finalTotal.toFixed(2)} MAD`,
        ``,
        `══════════════════════`,
        `شكراً لثقتكم بدار أولماس 🌿`,
        `سنتواصل معكم قريباً لتأكيد الطلب`,
      ].filter(line => line !== "").join("\n");

      window.open(`https://wa.me/${WA_PHONE}?text=${encodeURIComponent(lines)}`, "_blank");

      /* 5. Success */
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });

    } catch (err) {
      console.error("Order error:", err);
      alert(t("checkout.order_error"));
    } finally { setPlacing(false); }
  };

  /* ── Redirect if cart empty ── */
  if (cart.length === 0 && step !== 3) {
    return (
      <div style={{ minHeight: "100vh", background: "#f5f0e8", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 20 }}>
        <style>{CSS}</style>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, color: "#5a4e35" }}>{t("checkout.empty_cart")}</p>
        <button className="co-btn-primary" style={{ maxWidth: 260 }} onClick={() => navigate("/products")}>{t("checkout.explore_collection")}</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f5f0e8", fontFamily: "'Cormorant Garamond', Georgia, serif", overflowX: "hidden", position: "relative" }}>
      <style>{CSS}</style>

      {/* Background pattern */}
      <div className="co-bg" aria-hidden="true" />
      <div className="co-glow co-glow-1" aria-hidden="true" />
      <div className="co-glow co-glow-2" aria-hidden="true" />

      <div className="co-container">

        {/* ── HEADER ── */}
        <div className="co-header">
          <div className="co-logo">
            <div className="co-logo-mark">
              <svg width="28" height="28" viewBox="0 0 40 40" fill="none">
                <path d="M20 5 Q28 10 28 20 Q28 32 20 36 Q12 32 12 20 Q12 10 20 5Z" fill="#2d5a3d" opacity=".85"/>
                <path d="M20 12 L20 32 M14 18 Q20 24 26 18" stroke="#c9a84c" strokeWidth="1.5" fill="none"/>
                <rect x="16" y="28" width="8" height="5" rx="1" fill="#c9a84c" opacity=".7"/>
              </svg>
            </div>
            <div>
              <div className="co-logo-name">{t("brand.name")}</div>
              <div className="co-logo-tag">{t("checkout.logo_tag")}</div>
            </div>
          </div>

          {/* Progress steps */}
          <div className="co-steps">
            {[
              { n: 1, label: t("checkout.step_shipping") },
              { n: 2, label: t("checkout.step_review")   },
              { n: 3, label: t("checkout.step_confirmed")},
            ].map(({ n, label }) => (
              <div key={n} className={`co-step${step === n ? " active" : step > n ? " done" : ""}`}>
                <div className="co-step-dot">
                  {step > n ? <CheckIcon /> : <span>{n}</span>}
                </div>
                <span className="co-step-label">{label}</span>
                {n < 3 && <div className={`co-step-line${step > n ? " done" : ""}`} />}
              </div>
            ))}
          </div>
        </div>

        <div className="co-gold-bar" />

        {/* ════════════════ STEP 1 — Shipping Form ════════════════ */}
        {step === 1 && (
          <div className="co-grid">

            {/* LEFT – shipping info */}
            <div className="co-panel">
              <h2 className="co-panel-title">{t("checkout.shipping_info")}</h2>

              <div className="co-field-group">
                {[
                  { label: t("checkout.full_name_label"),     name: "name",  type: "text",  ph: t("checkout.full_name_ph") },
                  { label: t("checkout.email_label"),   name: "email", type: "email", ph: t("checkout.email_ph") },
                  { label: t("checkout.phone_label"),  name: "phone", type: "tel",   ph: t("checkout.phone_ph") },
                ].map(({ label, name, type, ph }) => (
                  <div key={name} className="co-field">
                    <label className="co-field-label">{label}</label>
                    <input
                      name={name} value={form[name]} type={type}
                      placeholder={ph} onChange={handleChange}
                      className={`co-input${errors[name] ? " error" : ""}`}
                      onFocus={e => e.target.style.borderColor = "#c9a84c"}
                      onBlur={e  => e.target.style.borderColor = errors[name] ? "#c0392b" : "#ddd5be"}
                    />
                    {errors[name] && <span className="co-field-error">{errors[name]}</span>}
                  </div>
                ))}
              </div>

              <div className="co-field-label" style={{ marginBottom: 8 }}>{t("checkout.delivery_address_label")}</div>
              {[
                { name: "address", ph: t("checkout.street_ph") },
                { name: "city",    ph: t("checkout.city_ph") },
                { name: "zip",     ph: t("checkout.zip_ph") },
              ].map(({ name, ph }) => (
                <input key={name} name={name} value={form[name]} placeholder={ph}
                  onChange={handleChange}
                  className={`co-input${errors.address && name === "address" ? " error" : ""}`}
                  style={{ marginBottom: 10 }}
                  onFocus={e => e.target.style.borderColor = "#c9a84c"}
                  onBlur={e  => e.target.style.borderColor = "#ddd5be"}
                />
              ))}
              {errors.address && <span className="co-field-error">{errors.address}</span>}

              {/* Map picker */}
              <MapPicker onLocationSelect={loc => {
                setLocationPin(loc);
                const parts = loc.label.split(",");
                setForm(f => ({ ...f, address: parts[0] || f.address, city: parts[1]?.trim() || f.city }));
                if (errors.address) setErrors(prev => ({ ...prev, address: "" }));
              }} />

              {/* Order notes */}
              <div className="co-field" style={{ marginTop: 4 }}>
                <label className="co-field-label">{t("checkout.notes_label")}</label>
                <textarea
                  name="notes" value={form.notes} onChange={handleChange}
                  placeholder={t("checkout.notes_ph")}
                  className="co-input" rows={3}
                  style={{ resize: "vertical", minHeight: 70 }}
                  onFocus={e => e.target.style.borderColor = "#c9a84c"}
                  onBlur={e  => e.target.style.borderColor = "#ddd5be"}
                />
              </div>

              {/* Save info */}
              <label className="co-save-label">
                <input type="checkbox" name="save" checked={form.save} onChange={handleChange}
                  style={{ accentColor: "#c9a84c", width: 16, height: 16 }}
                />
                <span>{t("checkout.save_info")}</span>
              </label>

              <div style={{ borderTop: "1px solid #e8e0cc", paddingTop: 18, marginTop: 18 }}>
                <div className="co-payment-mini">
                  <span style={{ fontFamily: "sans-serif", fontWeight: 900, fontSize: 12, color: "#1a1f71" }}>VISA</span>
                  <svg width="26" height="16" viewBox="0 0 30 20"><circle cx="11" cy="10" r="9" fill="#EB001B" opacity=".9"/><circle cx="19" cy="10" r="9" fill="#F79E1B" opacity=".9"/></svg>
                  <span style={{ fontFamily: "sans-serif", fontWeight: 700, fontSize: 11, color: "#009cde" }}>PayPal</span>
                </div>
              </div>
            </div>

            {/* RIGHT – order summary */}
            <div className="co-panel co-summary-panel">
              <h2 className="co-panel-title">{t("checkout.order_summary")}</h2>

              {/* Items */}
              <div className="co-order-items">
                {cart.map(item => (
                  <div key={item.id} className="co-order-item">
                    <div className="co-order-item-img">
                      {item.img
                        ? <img src={item.img} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                        : <span style={{ color: "rgba(201,168,76,.5)", fontSize: 20 }}>◈</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="co-order-item-name">{item.name}</p>
                      {item.size  && <p className="co-order-item-meta">{item.size}</p>}
                      {item.color && <p className="co-order-item-meta">{item.color}</p>}
                      <p className="co-order-item-meta">× {item.quantity}</p>
                    </div>
                    <span className="co-order-item-price">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Coupon */}
              <div className="co-coupon">
                <input
                  name="coupon" value={form.coupon}
                  onChange={e => { handleChange(e); setCouponMsg(null); }}
                  onKeyDown={e => e.key === "Enter" && applyCoupon()}
                  placeholder={t("checkout.coupon_ph")}
                  className="co-coupon-input"
                />
                <button className="co-coupon-btn" onClick={applyCoupon}>{t("checkout.apply")}</button>
              </div>
              {couponMsg && (
                <p className="co-coupon-msg" style={{ color: couponMsg.ok ? "#2d6a4f" : "#c0392b" }}>
                  {couponMsg.text}
                </p>
              )}

              {/* Breakdown */}
              <div className="co-breakdown">
                {[
                  { label: t("checkout.subtotal"),    val: `${subtotal.toFixed(2)} MAD` },
                  { label: t("checkout.shipping"),    val: t("checkout.free"),                    green: true },
                  { label: t("checkout.tax"),    val: `${tax.toFixed(2)} MAD` },
                  ...(discAmt > 0 ? [{ label: t("checkout.cart_discount"), val: `-${discAmt.toFixed(2)} MAD`, red: true }] : []),
                  ...(extraDisc > 0 ? [{ label: t("checkout.promo", { percent: extraDisc }), val: `-${Math.round(subtotal * extraDisc / 100).toFixed(2)} MAD`, red: true }] : []),
                ].map(({ label, val, green, red }) => (
                  <div key={label} className="co-breakdown-row">
                    <span style={{ color: "#8a7a55" }}>{label}</span>
                    <span style={{ color: green ? "#2d6a4f" : red ? "#c0392b" : "#1a1a12", fontWeight: 600 }}>{val}</span>
                  </div>
                ))}
              </div>

              <div className="co-total-row">
                <span className="co-total-label">{t("checkout.order_total")}</span>
                <span className="co-total-amount">{finalTotal.toFixed(2)} MAD</span>
              </div>

              {/* Proceed button */}
              <button className="co-btn-primary" onClick={goReview}>
                {t("checkout.review_order")}
              </button>

              <div className="co-trust">
                <div className="co-trust-item"><LockIcon /> {t("checkout.trust_secure")}</div>
                <div className="co-trust-divider" />
                <div className="co-trust-item"><ShieldIcon /> {t("checkout.trust_protected")}</div>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════ STEP 2 — Review & Confirm ════════════════ */}
        {step === 2 && (
          <div className="co-review">
            <h2 className="co-panel-title" style={{ marginBottom: 28 }}>{t("checkout.review_title")}</h2>

            <div className="co-grid">
              {/* Shipping summary */}
              <div className="co-panel">
                <div className="co-review-section">
                  <h3 className="co-review-section-title">{t("checkout.shipping_to")}</h3>
                  <div className="co-review-row"><span>{t("checkout.review_name")}</span><span>{form.name}</span></div>
                  <div className="co-review-row"><span>{t("checkout.review_phone")}</span><span>{form.phone}</span></div>
                  {form.email && <div className="co-review-row"><span>{t("checkout.review_email")}</span><span>{form.email}</span></div>}
                  <div className="co-review-row">
                    <span>{t("checkout.review_address")}</span>
                    <span style={{ maxWidth: 260, textAlign: "right", lineHeight: 1.4 }}>
                      {location_pin ? location_pin.label : `${form.address}, ${form.city} ${form.zip}`}
                    </span>
                  </div>
                  {form.notes && <div className="co-review-row"><span>{t("checkout.review_notes")}</span><span style={{ maxWidth: 260, textAlign: "right" }}>{form.notes}</span></div>}
                </div>

                <button className="co-btn-outline" onClick={() => setStep(1)} style={{ marginTop: 20 }}>{t("checkout.edit_shipping")}</button>
              </div>

              {/* Order summary + confirm */}
              <div className="co-panel co-summary-panel">
                <h3 className="co-review-section-title" style={{ marginBottom: 16 }}>{t("checkout.items_count", { count: cart.length })}</h3>
                <div className="co-order-items">
                  {cart.map(item => (
                    <div key={item.id} className="co-order-item">
                      <div className="co-order-item-img">
                        {item.img
                          ? <img src={item.img} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                          : <span style={{ color: "rgba(201,168,76,.5)", fontSize: 20 }}>◈</span>}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p className="co-order-item-name">{item.name}</p>
                        <p className="co-order-item-meta">× {item.quantity}</p>
                      </div>
                      <span className="co-order-item-price">{(item.price * item.quantity).toFixed(2)} MAD</span>
                    </div>
                  ))}
                </div>

                <div className="co-total-row" style={{ marginTop: 20 }}>
                  <span className="co-total-label">{t("checkout.total_label")}</span>
                  <span className="co-total-amount">{finalTotal.toFixed(2)} MAD</span>
                </div>

                {/* WhatsApp confirm button */}
                <button
                  className="co-btn-whatsapp"
                  onClick={placeOrder}
                  disabled={placing}
                >
                  {placing ? <><SpinnerIcon /> {t("checkout.processing")}</> : <><WhatsAppIcon size={20} /> {t("checkout.place_order_wa")}</>}
                </button>

                <p className="co-wa-note">
                  {t("checkout.wa_note")}
                </p>

                <div className="co-trust" style={{ marginTop: 16 }}>
                  <div className="co-trust-item"><LockIcon /> {t("checkout.trust_secure")}</div>
                  <div className="co-trust-divider" />
                  <div className="co-trust-item"><ShieldIcon /> {t("checkout.trust_protected")}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════ STEP 3 — Success ════════════════ */}
        {step === 3 && (
          <div className="co-success">
            <div className="co-success-icon">
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#2d6a4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h2 className="co-success-title">{t("checkout.success_title")}</h2>
            {orderRef && (
              <p className="co-success-ref">{t("checkout.success_ref")}<strong>#{orderRef}</strong></p>
            )}
            <p className="co-success-sub">
              {t("checkout.success_sub")}
            </p>
            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 32 }}>
              <button className="co-btn-primary" style={{ maxWidth: 240 }} onClick={() => navigate("/products")}>
                {t("checkout.continue_shopping")}
              </button>
              <button className="co-btn-outline" style={{ maxWidth: 200 }} onClick={() => navigate("/")}>
                {t("checkout.go_home")}
              </button>
            </div>
          </div>
        )}

        {/* Payment footer */}
        {step !== 3 && (
          <div className="co-footer">
            <div className="co-payment-icons">
              <span style={{ fontFamily: "sans-serif", fontWeight: 900, fontSize: 13, color: "#1a1f71" }}>VISA</span>
              <svg width="28" height="18" viewBox="0 0 30 20"><circle cx="11" cy="10" r="9" fill="#EB001B" opacity=".9"/><circle cx="19" cy="10" r="9" fill="#F79E1B" opacity=".9"/></svg>
              <span style={{ fontFamily: "sans-serif", fontWeight: 700, fontSize: 11 }}>
                <span style={{ color: "#009cde" }}>Pay</span><span style={{ color: "#003087" }}>Pal</span>
              </span>
            </div>
            <div className="co-secure-note">
              <LockIcon /> <span>{t("checkout.secure_checkout_note")}</span>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

/* ════════════════════════════════════════
   CSS
════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Cinzel:wght@400;500;600&family=EB+Garamond:ital,wght@0,400;0,500;1,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  @keyframes co-spin { to { transform: rotate(360deg); } }
  @keyframes shimBarCo { 0%{background-position:-200% center}100%{background-position:200% center} }

  /* ── Background ── */
  .co-bg {
    position: fixed; inset: 0; pointer-events: none; z-index: 0; opacity: 0.05;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cpolygon points='60,5 115,35 115,85 60,115 5,85 5,35' fill='none' stroke='%23c9a84c' stroke-width='0.8'/%3E%3Ccircle cx='60' cy='60' r='8' fill='none' stroke='%23c9a84c' stroke-width='0.4'/%3E%3C/svg%3E");
    background-size: 120px 120px;
  }
  .co-glow { position: fixed; pointer-events: none; z-index: 0; border-radius: 50%; filter: blur(100px); }
  .co-glow-1 { width: 500px; height: 500px; top: -80px; right: -100px; background: radial-gradient(circle, rgba(201,168,76,.08) 0%, transparent 65%); }
  .co-glow-2 { width: 400px; height: 400px; bottom: 0; left: -80px;   background: radial-gradient(circle, rgba(45,90,61,.15) 0%, transparent 65%); }

  .co-container {
    position: relative; z-index: 1;
    max-width: 1060px; margin: 0 auto; padding: 0 40px 80px;
  }
  @media(max-width:700px){ .co-container { padding: 0 18px 60px; } }

  /* ── Header ── */
  .co-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 32px 0 24px; flex-wrap: wrap; gap: 20px;
  }
  .co-logo { display: flex; align-items: center; gap: 12px; }
  .co-logo-mark {
    width: 50px; height: 50px; border-radius: 50%;
    border: 2px solid #c9a84c;
    background: linear-gradient(135deg, #f5f0e8 60%, #e8dfc8 100%);
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 2px 14px rgba(201,168,76,.25);
  }
  .co-logo-name {
    font-family: 'Cinzel', serif; font-size: 15px;
    letter-spacing: 3px; color: #2d2a1e; font-weight: 600;
  }
  .co-logo-tag {
    font-size: 8px; letter-spacing: 2px; color: #8a7a55; margin-top: 2px;
    font-family: 'EB Garamond', serif;
  }

  /* Progress steps */
  .co-steps {
    display: flex; align-items: center; gap: 0;
  }
  .co-step {
    display: flex; align-items: center; gap: 8px;
  }
  .co-step-dot {
    width: 34px; height: 34px; border-radius: 50%;
    border: 2px solid #ddd5be;
    background: #f5f0e8;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Cinzel', serif; font-size: 12px; font-weight: 600; color: #b0a080;
    transition: all .3s; flex-shrink: 0;
  }
  .co-step.active .co-step-dot {
    border-color: #c9a84c;
    background: linear-gradient(135deg, #c9a84c, #a87c2a);
    color: #fff; box-shadow: 0 4px 14px rgba(201,168,76,.4);
  }
  .co-step.done .co-step-dot {
    border-color: #2d6a4f; background: #2d6a4f; color: #fff;
  }
  .co-step-label {
    font-family: 'Cinzel', serif; font-size: 9px; letter-spacing: 2px;
    color: #8a7a55; white-space: nowrap;
  }
  .co-step.active .co-step-label { color: #c9a84c; font-weight: 600; }
  .co-step.done  .co-step-label  { color: #2d6a4f; }
  .co-step-line {
    width: 40px; height: 1px;
    background: rgba(201,168,76,.25); margin: 0 4px; flex-shrink: 0;
  }
  .co-step-line.done { background: #2d6a4f; }

  /* Gold bar */
  .co-gold-bar {
    height: 1px; margin-bottom: 36px;
    background: linear-gradient(90deg, transparent, #c9a84c 30%, #f5d88a 50%, #c9a84c 70%, transparent);
    background-size: 200% auto;
    animation: shimBarCo 3.5s linear infinite; opacity: .45;
  }

  /* ── Grid ── */
  .co-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 28px; align-items: start;
  }
  @media(max-width:800px){ .co-grid { grid-template-columns: 1fr; } }

  /* ── Panel ── */
  .co-panel {
    background: rgba(255,253,248,.92);
    border: 1.5px solid #e0d5be; border-radius: 22px;
    padding: 32px 28px;
    box-shadow: 0 6px 40px rgba(201,168,76,.1);
    backdrop-filter: blur(8px);
  }
  .co-panel-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 26px; font-weight: 700; color: #2d2a1e; margin-bottom: 26px;
  }
  .co-summary-panel { position: sticky; top: 90px; }

  /* ── Form fields ── */
  .co-field-group { margin-bottom: 4px; }
  .co-field { margin-bottom: 16px; }
  .co-field-label {
    display: block; font-family: 'EB Garamond', serif;
    font-size: 13px; color: #5a4e35; margin-bottom: 7px; font-weight: 500;
  }
  .co-input {
    display: block; width: 100%;
    padding: 11px 14px; border: 1.5px solid #ddd5be; border-radius: 10px;
    background: #faf7f0; font-family: 'EB Garamond', serif;
    font-size: 14px; color: #3a3220; outline: none;
    transition: border-color .2s; margin-bottom: 0;
  }
  .co-input::placeholder { color: #b0a080; }
  .co-input.error { border-color: #c0392b; }
  textarea.co-input { resize: vertical; }
  .co-field-error {
    display: block; font-family: 'EB Garamond', serif;
    font-size: 12px; color: #c0392b; margin-top: 4px;
  }

  /* ── Map ── */
  .co-map-wrap { margin: 18px 0; }
  .co-map-header {
    display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px;
  }
  .co-locate-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 8px 16px; border: none; border-radius: 8px;
    background: linear-gradient(135deg, #c9a84c, #a87c2a);
    color: #fff; font-family: 'EB Garamond', serif; font-size: 12px; font-weight: 700;
    letter-spacing: .5px; cursor: pointer;
    box-shadow: 0 2px 10px rgba(201,168,76,.35);
    transition: opacity .2s; white-space: nowrap;
  }
  .co-locate-btn:disabled { background: #e8e0cc; color: #8a7a55; cursor: not-allowed; box-shadow: none; }
  .co-map-container {
    position: relative; border-radius: 14px; overflow: hidden;
    border: 1.5px solid #ddd5be;
    box-shadow: 0 4px 22px rgba(0,0,0,.1);
  }
  .co-map-styles {
    position: absolute; top: 10px; right: 10px; z-index: 1000;
    display: flex; flex-direction: column; gap: 4px;
  }
  .co-map-style-btn {
    padding: 5px 10px; border-radius: 6px; cursor: pointer;
    background: rgba(255,253,248,.94); border: 1.5px solid #ddd5be;
    font-family: 'EB Garamond', serif; font-size: 11px; color: #5a4e35;
    transition: all .18s; white-space: nowrap;
  }
  .co-map-style-btn.active {
    background: linear-gradient(135deg, #c9a84c, #a87c2a);
    border-color: #b8952a; color: #fff; font-weight: 700;
    box-shadow: 0 2px 10px rgba(201,168,76,.35);
  }
  .co-map-hint {
    position: absolute; bottom: 10px; left: 10px; z-index: 1000;
    background: rgba(255,253,248,.9); border: 1px solid #ddd5be;
    border-radius: 6px; padding: 4px 8px;
    font-family: 'EB Garamond', serif; font-size: 11px; color: #8a7a55;
    pointer-events: none; backdrop-filter: blur(4px);
  }
  .co-loc-error {
    font-family: 'EB Garamond', serif; font-size: 12px; color: #c0392b;
    margin-top: 8px; background: #fdf0f0; padding: 8px 12px;
    border-radius: 6px; border: 1px solid #f5c6c6;
  }
  .co-loc-label {
    display: flex; align-items: flex-start; gap: 8px;
    margin-top: 8px; padding: 10px 12px;
    background: linear-gradient(135deg, #f5f0e8, #faf7f2);
    border: 1.5px solid rgba(201,168,76,.4); border-radius: 10px;
    font-family: 'EB Garamond', serif; font-size: 12px; color: #3a3220; line-height: 1.5;
  }

  /* Save label */
  .co-save-label {
    display: flex; align-items: center; gap: 10px;
    cursor: pointer; margin: 18px 0;
    font-family: 'EB Garamond', serif; font-size: 13px; color: #5a4e35;
  }

  /* ── Order items ── */
  .co-order-items { margin-bottom: 20px; }
  .co-order-item {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 12px 0; border-bottom: 1px solid rgba(201,168,76,.12);
  }
  .co-order-item:last-child { border-bottom: none; }
  .co-order-item-img {
    width: 58px; height: 58px; border-radius: 10px; flex-shrink: 0;
    background: linear-gradient(135deg, #e8d8a0, rgba(201,168,76,.15));
    display: flex; align-items: center; justify-content: center; overflow: hidden;
  }
  .co-order-item-name {
    font-family: 'Cormorant Garamond', serif; font-size: 15px; font-weight: 600;
    color: #2d2a1e; line-height: 1.3; margin-bottom: 3px;
  }
  .co-order-item-meta {
    font-family: 'EB Garamond', serif; font-size: 12px; color: #8a7a55; line-height: 1.4;
  }
  .co-order-item-price {
    font-family: 'Cormorant Garamond', serif; font-size: 16px; font-weight: 700;
    color: #1a1a12; flex-shrink: 0; padding-top: 2px;
  }

  /* ── Coupon ── */
  .co-coupon {
    display: flex; margin: 16px 0 4px;
    border: 1.5px solid rgba(201,168,76,.3); border-radius: 10px; overflow: hidden;
    background: #f8f3e8;
  }
  .co-coupon-input {
    flex: 1; padding: 10px 14px; border: none; background: transparent;
    font-family: 'EB Garamond', serif; font-size: 14px; color: #3a3220; outline: none;
  }
  .co-coupon-input::placeholder { color: #b0a080; }
  .co-coupon-btn {
    padding: 10px 16px; border: none; background: #c9a84c; color: #fff;
    font-family: 'Cinzel', serif; font-size: 10px; letter-spacing: 2px; cursor: pointer;
    transition: background .2s;
  }
  .co-coupon-btn:hover { background: #a87c2a; }
  .co-coupon-msg { font-family: 'EB Garamond', serif; font-size: 12px; margin-top: 4px; }

  /* ── Breakdown ── */
  .co-breakdown { padding: 16px 0 4px; border-top: 1px solid rgba(201,168,76,.2); }
  .co-breakdown-row {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 10px; font-family: 'EB Garamond', serif; font-size: 15px;
  }
  .co-total-row {
    display: flex; justify-content: space-between; align-items: baseline;
    padding: 16px 0 0; margin-top: 6px; border-top: 2px solid #c9a84c;
  }
  .co-total-label { font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 2px; color: #1a1a12; }
  .co-total-amount { font-family: 'Cormorant Garamond', serif; font-size: 34px; font-weight: 700; color: #1a1a12; }

  /* ── Buttons ── */
  .co-btn-primary {
    display: block; width: 100%; margin-top: 22px; padding: 16px;
    background: linear-gradient(135deg, #c9a84c 0%, #a87c2a 100%);
    border: none; border-radius: 14px; color: #fff;
    font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 2.5px;
    cursor: pointer;
    box-shadow: 0 8px 26px rgba(201,168,76,.42);
    transition: transform .2s, box-shadow .2s, opacity .2s;
    text-align: center;
  }
  .co-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 34px rgba(201,168,76,.52); }

  .co-btn-whatsapp {
    display: flex; align-items: center; justify-content: center; gap: 12px;
    width: 100%; margin-top: 22px; padding: 16px;
    background: #132918; border: 2px solid rgba(201,168,76,.3);
    border-radius: 14px; color: #fff;
    font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 1.5px;
    cursor: pointer;
    box-shadow: 0 8px 28px rgba(19,41,24,.3);
    transition: background .25s, transform .2s, box-shadow .25s, border-color .25s;
  }
  .co-btn-whatsapp:hover:not(:disabled) {
    background: #1a5c35; transform: translateY(-2px);
    box-shadow: 0 12px 36px rgba(26,92,53,.45);
    border-color: rgba(37,210,102,.45);
  }
  .co-btn-whatsapp:disabled { opacity: .6; cursor: not-allowed; }

  .co-btn-outline {
    display: block; width: 100%; padding: 14px;
    background: transparent; border: 1.5px solid rgba(201,168,76,.4);
    border-radius: 14px; color: #5a4e35;
    font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 2px;
    cursor: pointer; transition: all .2s; text-align: center;
  }
  .co-btn-outline:hover { background: rgba(201,168,76,.07); border-color: #c9a84c; color: #c9a84c; }

  .co-wa-note {
    font-family: 'EB Garamond', serif; font-size: 12px; color: #8a7a55;
    text-align: center; margin-top: 12px; line-height: 1.6;
  }

  /* ── Trust ── */
  .co-trust {
    display: flex; align-items: center; justify-content: center; gap: 14px;
    margin-top: 18px; padding-top: 16px; border-top: 1px solid rgba(201,168,76,.2);
  }
  .co-trust-item {
    display: flex; align-items: center; gap: 5px;
    font-size: 11px; color: #8a7a55; letter-spacing: .5px;
    font-family: 'EB Garamond', serif;
  }
  .co-trust-divider { width: 1px; height: 14px; background: rgba(201,168,76,.3); }

  /* ── Review step ── */
  .co-review { }
  .co-review-section { margin-bottom: 8px; }
  .co-review-section-title {
    font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 2.5px;
    color: #c9a84c; margin-bottom: 16px;
  }
  .co-review-row {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 9px 0; border-bottom: 1px solid rgba(201,168,76,.12);
    font-family: 'EB Garamond', serif; font-size: 14px; gap: 16px;
  }
  .co-review-row span:first-child { color: #8a7a55; flex-shrink: 0; }
  .co-review-row span:last-child  { color: #2d2a1e; font-weight: 500; }

  /* ── Payment mini ── */
  .co-payment-mini {
    display: flex; align-items: center; gap: 14px; opacity: .65;
  }
  .co-payment-icons {
    display: flex; align-items: center; gap: 14px; justify-content: center; opacity: .6;
  }

  /* ── Success ── */
  .co-success {
    text-align: center; padding: 60px 24px;
    max-width: 560px; margin: 0 auto;
  }
  .co-success-icon {
    width: 84px; height: 84px; border-radius: 50%;
    background: rgba(45,106,79,.1); border: 2px solid rgba(45,106,79,.3);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 26px;
  }
  .co-success-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 48px; font-weight: 300; color: #1a1a12; margin-bottom: 10px;
  }
  .co-success-ref {
    font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 2px;
    color: #c9a84c; margin-bottom: 16px;
  }
  .co-success-sub {
    font-family: 'EB Garamond', serif; font-size: 15px;
    color: #8a7a55; line-height: 1.7; margin-bottom: 8px;
  }

  /* ── Footer ── */
  .co-footer {
    margin-top: 48px; padding: 28px 0 0;
    border-top: 1px solid rgba(201,168,76,.2); text-align: center;
  }
  .co-secure-note {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    margin-top: 12px; font-family: 'EB Garamond', serif;
    font-size: 13px; color: #8a7a55;
  }
`;